/**
 * Partial-lead capture (Abandoned Leads).
 *
 * Posts whatever contact info we've collected so far to
 * `POST /api/leads/partial`. Deduped on the backend by `session_id`,
 * so repeat calls with new info progressively enrich the same row.
 *
 * Called from:
 *   • PhoneStep after a visitor successfully verifies their phone →
 *     captures phone + the funnel answers accumulated so far.
 *   • EmailStep's `beforeunload` beacon so an email typed-but-not-
 *     submitted still lands in Admin → Abandoned Leads.
 */
import { api } from '@/lib/api';
import { getSessionId } from '@/lib/tracking';

/** Same-origin API base for abandoned-lead writes.
 *
 * Partial leads MUST land in the same backend that served the page the
 * visitor is on — otherwise a production visitor on apply.thelemonpros.com
 * would silently write to the preview Mongo whenever the build bakes the
 * wrong `REACT_APP_BACKEND_URL`. Using `window.location.origin` at call
 * time is immune to build-env drift: K8s ingress routes `/api/*` to
 * backend on every environment, so this works on preview and production
 * alike. We also override axios per-request so `savePartialLead` follows
 * the same rule. */
function _partialBase() {
  try {
    return (typeof window !== 'undefined' && window.location?.origin) || '';
  } catch {
    return '';
  }
}

function _buildPayload(extra = {}, answers = {}) {
  const stickyRaw = (() => {
    try { return JSON.parse(sessionStorage.getItem('lp_sticky_attribution_v1') || 'null'); }
    catch { return null; }
  })();
  const sticky = stickyRaw && (Date.now() - (stickyRaw.set_at || 0) < 2 * 60 * 60 * 1000)
    ? stickyRaw : null;

  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const params = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search) : new URLSearchParams();

  return {
    session_id:    getSessionId(),
    source_page:   answers.source_page || '',
    step_reached:  extra.step_reached || '',
    phone:         answers.phone || extra.phone || '',
    email:         answers.email || extra.email || '',
    first_name:    answers.first_name || '',
    last_name:     answers.last_name  || '',
    full_name:     answers.full_name  || '',
    vehicle_year:  answers.vehicle_year  || answers.year  || '',
    vehicle_make:  answers.vehicle_make  || answers.make  || '',
    vehicle_model: answers.vehicle_model || answers.model || '',
    network:       sticky?.source || answers.attribution_source || '',
    utm_source:    sticky?.utm_source || params.get('utm_source') || answers.utm_source || '',
    utm_medium:    sticky?.utm_medium || params.get('utm_medium') || answers.utm_medium || '',
    utm_campaign:  params.get('utm_campaign') || '',
    gclid:         params.get('gclid')  || '',
    oppref:        params.get('oppref') || '',
    fbclid:        params.get('fbclid') || '',
    entry_path:    sticky?.entry_path || answers.entry_path || path,
    language:      answers.lang || '',
  };
}

/** Fire-and-forget partial save. Debounced upstream — call freely.
 *  Forced same-origin so prod visitors never write to preview Mongo. */
export function savePartialLead(extra = {}, answers = {}) {
  try {
    const base = _partialBase();
    const url  = base ? `${base}/api/leads/partial` : '/api/leads/partial';
    api.post(url, _buildPayload(extra, answers)).catch(() => {});
  } catch { /* ignore */ }
}

/** sendBeacon version for `beforeunload` so the request survives tab close.
 *  Always same-origin — see _partialBase() for the rationale. */
export function beaconPartialLead(extra = {}, answers = {}) {
  try {
    const base = _partialBase();
    const url  = base ? `${base}/api/leads/partial` : '/api/leads/partial';
    const body = JSON.stringify(_buildPayload(extra, answers));
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon?.(url, blob);
  } catch { /* ignore */ }
}
